export const groupId = (key) => `group:${key}`;
export const itemId = (key) => `item:${key}`;

const parseId = (id) => {
  const [kind, key] = String(id).split(':');
  return { kind, key };
};

const groupOfItem = (layout, itemKey) => layout.find((group) => group.items.some((item) => item.key === itemKey));

const updateGroup = (layout, groupKey, change) => layout.map((group) => (group.key === groupKey ? { ...group, ...change(group) } : group));

const updateItem = (layout, itemKey, change) => layout.map((group) => ({
  ...group,
  items: group.items.map((item) => (item.key === itemKey ? { ...item, ...change(item) } : item)),
}));

const moveGroup = (layout, groupKey, toIndex) => {
  const moving = layout.find((group) => group.key === groupKey);
  if (!moving) return layout;
  const rest = layout.filter((group) => group.key !== groupKey);
  rest.splice(Math.min(toIndex, rest.length), 0, moving);
  return rest;
};

const moveItem = (layout, itemKey, toGroupKey, toIndex) => {
  const source = groupOfItem(layout, itemKey);
  if (!source) return layout;
  const moving = source.items.find((item) => item.key === itemKey);
  const without = layout.map((group) => ({ ...group, items: group.items.filter((item) => item.key !== itemKey) }));
  return without.map((group) => {
    if (group.key !== toGroupKey) return group;
    const items = [...group.items];
    items.splice(Math.min(toIndex, items.length), 0, moving);
    return { ...group, items };
  });
};

export function layoutReducer(state, action) {
  switch (action.type) {
    case 'setField':
      return { ...state, [action.field]: action.value };
    case 'moveGroup':
      return { ...state, layout: moveGroup(state.layout, action.groupKey, action.toIndex) };
    case 'moveItem':
      return { ...state, layout: moveItem(state.layout, action.itemKey, action.toGroupKey, action.toIndex) };
    case 'renameGroup':
      return { ...state, layout: updateGroup(state.layout, action.groupKey, () => ({ label: action.label })) };
    case 'renameItem':
      return { ...state, layout: updateItem(state.layout, action.itemKey, () => ({ label: action.label })) };
    case 'toggleGroupHidden':
      return { ...state, layout: updateGroup(state.layout, action.groupKey, (group) => ({ hidden: !group.hidden })) };
    case 'toggleItemHidden':
      return { ...state, layout: updateItem(state.layout, action.itemKey, (item) => ({ hidden: !item.hidden })) };
    default:
      return state;
  }
}

const compactEntry = ({ key, label, hidden }) => ({
  key,
  ...(label && label.trim() ? { label: label.trim() } : {}),
  ...(hidden ? { hidden: true } : {}),
});

export const toPayload = (state) => ({
  navigation_type: {
    name: state.name,
    contact_label: state.contactLabel,
    segment_placeholder: state.segmentPlaceholder,
    artist_fan_features: state.artistFanFeatures,
    layout: state.layout.map((group) => ({ ...compactEntry(group), items: group.items.map(compactEntry) })),
  },
});

export const dragEndAction = (layout, activeId, overId) => {
  if (!overId || activeId === overId) return null;
  const active = parseId(activeId);
  const over = parseId(overId);
  const overGroupKey = over.kind === 'group' ? over.key : groupOfItem(layout, over.key)?.key;
  if (!overGroupKey) return null;

  if (active.kind === 'group') {
    return { type: 'moveGroup', groupKey: active.key, toIndex: layout.findIndex((group) => group.key === overGroupKey) };
  }

  if (over.kind === 'group' && groupOfItem(layout, active.key)?.key === overGroupKey) return null;

  const target = layout.find((group) => group.key === overGroupKey);
  const toIndex = over.kind === 'group' ? target.items.length : target.items.findIndex((item) => item.key === over.key);
  return { type: 'moveItem', itemKey: active.key, toGroupKey: overGroupKey, toIndex };
};

export const defaultLabels = (registry) => ({
  groups: Object.fromEntries(registry.groups.map((group) => [group.key, group.label])),
  items: Object.fromEntries(registry.groups.flatMap((group) => group.items.map((item) => [item.key, item.label]))),
});

export const filterDroppables = (layout, activeId, containers) => {
  const emptyGroupIds = layout.filter((group) => group.items.length === 0).map((group) => groupId(group.key));
  const kindOf = (container) => parseId(container.id).kind;
  if (parseId(activeId).kind === 'group') return containers.filter((container) => kindOf(container) === 'group');
  return containers.filter((container) => kindOf(container) === 'item' || emptyGroupIds.includes(container.id));
};
